import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Check, Download, Eye, Code, FileImage, Image as ImageIcon, Loader2, Edit, Plus } from "lucide-react";
import { toast } from "sonner";
import { EditDialog } from "@/components/EditDialog";
import { AdditionalVisualDialog } from "@/components/AdditionalVisualDialog";
import { useStreamingVisualCreator } from "@/hooks/useStreamingVisualCreator";
import { useStreamingHumanizer } from "@/hooks/useStreamingHumanizer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { FeedbackButtons } from "@/components/FeedbackButtons";

interface VisualVersion {
  id: string;
  svg: string;
  dataUrl: string | null;
  label: string;
  timestamp: number;
}

interface OutputDisplayProps {
  originalText: string;
  humanizedText: string;
  includeVisuals: boolean;
  mobilePreview?: boolean;
  isStreaming?: boolean;
  visualContent?: {svg: string | null, dataUrl: string | null};
  visualVersions?: VisualVersion[];
  isGeneratingVisuals?: boolean;
  currentStep?: 'text' | 'visuals' | 'complete';
  onContentUpdate?: (newContent: string, type: 'text' | 'visual') => void;
}

export const OutputDisplay = ({ 
  originalText, 
  humanizedText, 
  includeVisuals, 
  mobilePreview = false, 
  isStreaming = false,
  visualContent,
  visualVersions = [],
  isGeneratingVisuals = false,
  currentStep = 'complete',
  onContentUpdate
}: OutputDisplayProps) => {
  const { user } = useAuth();
  const { streamingState: visualStreamingState, streamGenerateVisual } = useStreamingVisualCreator();
  const { streamingState: textStreamingState, streamHumanizeText } = useStreamingHumanizer();
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'rendered' | 'code'>('rendered');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editType, setEditType] = useState<'text' | 'visual'>('text');
  const [internalVisualVersions, setInternalVisualVersions] = useState<VisualVersion[]>(visualVersions);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const [isAdditionalVisualDialogOpen, setIsAdditionalVisualDialogOpen] = useState(false);

  // Initialize versions from props or visualContent
  useEffect(() => {
    // First priority: use visualVersions prop if available
    if (visualVersions && visualVersions.length > 0 && internalVisualVersions.length === 0) {
      setInternalVisualVersions(visualVersions);
      return;
    }
    
    // Fallback: create initial version from visualContent if no versions exist
    if (visualContent?.svg && internalVisualVersions.length === 0) {
      const initialVersion: VisualVersion = {
        id: `v0-${Date.now()}`,
        svg: visualContent.svg,
        dataUrl: visualContent.dataUrl,
        label: 'v0',
        timestamp: Date.now()
      };
      setInternalVisualVersions([initialVersion]);
    }
  }, [visualVersions, visualContent?.svg, internalVisualVersions.length]);

  // Update versions when new visualVersions prop is received
  useEffect(() => {
    if (visualVersions && visualVersions.length > internalVisualVersions.length) {
      setInternalVisualVersions(visualVersions);
    }
  }, [visualVersions, internalVisualVersions.length]);

  const currentVisual = internalVisualVersions[selectedVersionIndex] || visualContent;

  // Auto-generate visual when includeVisuals is true but no visual exists
  useEffect(() => {
    if (includeVisuals && humanizedText && !currentVisual?.svg && !isGeneratingVisuals && !visualStreamingState.isGenerating) {
      const generateAutoVisual = async () => {
        try {
          await streamGenerateVisual(humanizedText, 'auto', '16:9');
        } catch (error) {
          console.error('Failed to auto-generate visual:', error);
        }
      };
      
      generateAutoVisual();
    }
  }, [includeVisuals, humanizedText, currentVisual?.svg, isGeneratingVisuals, visualStreamingState.isGenerating, streamGenerateVisual]);

  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set(prev).add(itemId));
      toast.success("Copied to clipboard!");
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const parseMarkdownToHtml = (text: string): string => {
    let html = text;
    
    // Headers (## -> h2, ### -> h3, etc.)
    html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
    
    // Bold text **text** -> <strong>text</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic text *text* -> <em>text</em>
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Line breaks
    html = html.replace(/\n/g, '<br>');
    
    return html;
  };

  const renderContent = (content: string) => {
    if (!includeVisuals || (!currentVisual?.svg && currentStep !== 'visuals')) {
      return (
        <div className="prose prose-invert prose-purple max-w-none">
          <div 
            className="text-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(content) }}
          />
        </div>
      );
    }

    // Show visual generation progress
    if (currentStep === 'visuals' && isGeneratingVisuals) {
      return (
        <div className="space-y-6">
          <div className="prose prose-invert prose-purple max-w-none">
            <div 
              className="text-foreground leading-relaxed"
              dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(content) }}
            />
          </div>
          <div className="bg-muted/5 rounded-lg p-8 border border-border text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-sm text-muted-foreground">
              Generating visual explanations to enhance understanding...
            </p>
          </div>
        </div>
      );
    }

    // Show content with embedded visuals or separate visual
    if (currentVisual?.svg) {
      return (
        <div className="space-y-6">
          <div className="prose prose-invert prose-purple max-w-none">
            <div 
              className="text-foreground leading-relaxed"
              dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(content) }}
            />
          </div>
          
          {/* Version Selector */}
          {internalVisualVersions.length > 1 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">Visual Version:</span>
              <div className="flex gap-1 flex-wrap">
                {internalVisualVersions.map((version, index) => (
                  <Button
                    key={version.id}
                    size="sm"
                    variant={selectedVersionIndex === index ? "default" : "outline"}
                    onClick={() => setSelectedVersionIndex(index)}
                    className="h-7 px-2 text-xs"
                  >
                    {version.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="relative group">
            <div className="bg-muted/5 rounded-lg p-4 border border-border">
              {viewMode === 'rendered' ? (
                <div 
                  className="flex justify-center w-full min-h-[200px] relative overflow-hidden"
                  style={{ maxWidth: '100%' }}
                >
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(currentVisual.svg) }}
                  />
                </div>
              ) : (
                <pre className="text-sm text-muted-foreground overflow-x-auto">
                  <code>{currentVisual.svg}</code>
                </pre>
              )}
            </div>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 flex-wrap">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setViewMode(viewMode === 'rendered' ? 'code' : 'rendered')}
                className="h-8 w-8 p-0"
                title={viewMode === 'rendered' ? 'View code' : 'View rendered'}
              >
                {viewMode === 'rendered' ? <Code className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => copyToClipboard(currentVisual.svg, 'visual-svg')}
                className="h-8 w-8 p-0"
                title="Copy SVG code"
              >
                {copiedItems.has('visual-svg') ? (
                  <Check className="w-3 h-3 text-success" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => downloadSvg(currentVisual.svg, selectedVersionIndex)}
                className="h-8 w-8 p-0"
                title="Download SVG"
              >
                <FileImage className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => downloadPng(currentVisual.svg, selectedVersionIndex)}
                className="h-8 w-8 p-0"
                title="Download PNG (High Quality)"
              >
                <ImageIcon className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => openEditDialog('visual')}
                className="h-8 w-8 p-0"
                title="Edit Visual"
              >
                <Edit className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsAdditionalVisualDialogOpen(true)}
                className="h-8 w-8 p-0"
                title="Generate Additional Visual"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Parse content with embedded SVGs (fallback)
    const parts = content.split(/(<svg[\s\S]*?<\/svg>)/gi);
    
    return (
      <div className="space-y-6">
        {parts.map((part, index) => {
          if (part.trim().toLowerCase().startsWith('<svg')) {
            return (
              <div key={index} className="relative group">
                <div className="bg-muted/5 rounded-lg p-4 border border-border">
                  {viewMode === 'rendered' ? (
                    <div 
                      className="flex justify-center w-full min-h-[200px] relative overflow-hidden"
                      style={{ maxWidth: '100%' }}
                    >
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        dangerouslySetInnerHTML={{ __html: sanitizeSvg(part) }}
                      />
                    </div>
                  ) : (
                    <pre className="text-sm text-muted-foreground overflow-x-auto">
                      <code>{part}</code>
                    </pre>
                  )}
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setViewMode(viewMode === 'rendered' ? 'code' : 'rendered')}
                    className="h-8 w-8 p-0"
                    title={viewMode === 'rendered' ? 'View code' : 'View rendered'}
                  >
                    {viewMode === 'rendered' ? <Code className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => copyToClipboard(part, `svg-${index}`)}
                    className="h-8 w-8 p-0"
                    title="Copy SVG code"
                  >
                    {copiedItems.has(`svg-${index}`) ? (
                      <Check className="w-3 h-3 text-success" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => downloadSvg(part, index)}
                    className="h-8 w-8 p-0"
                    title="Download SVG"
                  >
                    <FileImage className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => downloadPng(part, index)}
                    className="h-8 w-8 p-0"
                    title="Download PNG (High Quality)"
                  >
                    <ImageIcon className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openEditDialog('visual')}
                    className="h-8 w-8 p-0"
                    title="Edit Visual"
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setIsAdditionalVisualDialogOpen(true)}
                    className="h-8 w-8 p-0"
                    title="Generate Additional Visual"
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          } else if (part.trim()) {
            return (
              <div key={index} className="prose prose-invert prose-purple max-w-none">
                <div 
                  className="text-foreground leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: part.trim() }}
                />
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const downloadAsFile = () => {
    const element = document.createElement("a");
    const file = new Blob([humanizedText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = "humanized-text.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success("Downloaded successfully!");
  };

  const sanitizeSvg = (input: string) => {
    let svg = input.trim();

    // Remove any script tags for safety
    svg = svg.replace(/<script[\s\S]*?<\/script>/gi, '');

    // Ensure XML namespaces exist on <svg>
    svg = svg.replace(/<svg(\s[^>]*)?>/i, (m) => {
      let tag = m;
      if (!/xmlns=/.test(tag)) tag = tag.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      if (!/xmlns:xlink=/.test(tag)) tag = tag.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
      return tag;
    });

    // Make SVG responsive: width 100%, height auto, preserve aspect ratio
    svg = svg.replace(/<svg([^>]*)>/i, (m, attrs) => {
      let a = attrs || '';
      a = a.replace(/\swidth="[^"]*"/i, '');  
      a = a.replace(/\sheight="[^"]*"/i, '');
      const hasPAR = /preserveAspectRatio=/i.test(a);
      const par = hasPAR ? '' : ' preserveAspectRatio="xMidYMid meet"';
      const styleAttr = /style=/i.test(a) ? '' : ' style="max-width:100%;height:auto;overflow:visible;"';
      return `<svg${a} width="100%" height="auto"${par}${styleAttr}>`;
    });

    // Fix text positioning and overflow issues
    svg = svg.replace(/<text([^>]*)>/gi, (m, attrs) => {
      // Ensure text has proper overflow handling and positioning
      if (!/text-anchor/.test(attrs)) {
        attrs += ' text-anchor="start"';
      }
      if (!/dominant-baseline/.test(attrs)) {
        attrs += ' dominant-baseline="auto"';
      }
      return `<text${attrs} style="overflow:visible;word-wrap:break-word;">`;
    });

    // Ensure proper container sizing for text elements
    svg = svg.replace(/<foreignObject([^>]*)>/gi, (m, attrs) => {
      if (!/overflow/.test(attrs)) {
        return `<foreignObject${attrs} style="overflow:visible;">`;
      }
      return m;
    });

    // Escape unencoded ampersands to prevent XML parse errors (e.g., Q&A)
    svg = svg.replace(/&(?!amp;|lt;|gt;|quot;|apos;|#\d+;|#x[0-9A-Fa-f]+;)/g, '&amp;');

    // Inject Font Awesome fonts for icons only (not regular text)  
    const faStyle = `
  <defs>
    <style><![CDATA[
      @font-face {
        font-family: 'Font Awesome 6 Free';
        src: url('/fonts/fa-solid-900.woff2') format('woff2');
        font-weight: 900;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: 'Font Awesome 6 Brands';
        src: url('/fonts/fa-brands-400.woff2') format('woff2');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      }
      .fa, .fas, .far, .fab, .fal, .fad,
      text[class*="fa"], tspan[class*="fa"] {
        font-family: 'Font Awesome 6 Free', 'Font Awesome 6 Brands' !important;
        font-weight: 900;
      }
      text:not([class*="fa"]), tspan:not([class*="fa"]) {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: normal;
      }
      text, tspan {
        overflow: visible !important;
        word-wrap: break-word;
      }
    ]]></style>
  </defs>
`;
    if (svg.includes('<svg')) {
      svg = svg.replace(/<svg([^>]*)>/i, (m) => `${m}\n${faStyle}`);
    }

    return svg;
  };

  const downloadSvg = (svgContent: string, index: number) => {
    try {
      let cleanSvg = svgContent.trim();
      
      if (!cleanSvg.startsWith('<?xml')) {
        cleanSvg = '<?xml version="1.0" encoding="UTF-8"?>\n' + cleanSvg;
      }
      
      if (!cleanSvg.includes('<!DOCTYPE')) {
        cleanSvg = cleanSvg.replace('<?xml version="1.0" encoding="UTF-8"?>', 
          '<?xml version="1.0" encoding="UTF-8"?>\n<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">');
      }

      cleanSvg = sanitizeSvg(cleanSvg);

      const element = document.createElement("a");
      const file = new Blob([cleanSvg], { type: 'image/svg+xml;charset=utf-8' });
      element.href = URL.createObjectURL(file);
      element.download = `diagram-${index + 1}.svg`;
      
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      setTimeout(() => URL.revokeObjectURL(element.href), 100);
      
      toast.success("SVG downloaded successfully!");
    } catch (error) {
      console.error("SVG download failed:", error);
      toast.error("Failed to download SVG. Copying code to clipboard instead.");
      copyToClipboard(svgContent, `svg-fallback-${index}`);
    }
  };
  const downloadPng = async (svgContent: string, index: number) => {
    try {
      // Clean and prepare SVG
      let svg = sanitizeSvg(svgContent);

      // Remove any external CSS imports that won't work in rasterization
      svg = svg.replace(/@import[^;]+;?/g, '');

      // Dimensions from viewBox or width/height
      const viewBoxMatch = svg.match(/viewBox\s*=\s*"([0-9.\s]+)"/i);
      let width = 1200, height = 800;
      if (viewBoxMatch) {
        const parts = viewBoxMatch[1].trim().split(/\s+/).map(Number);
        if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
          width = Math.round(parts[2]);
          height = Math.round(parts[3]);
        }
      } else {
        const wMatch = svg.match(/width\s*=\s*"([0-9.]+)(px)?"/i);
        const hMatch = svg.match(/height\s*=\s*"([0-9.]+)(px)?"/i);
        if (wMatch && hMatch) {
          width = Math.round(parseFloat(wMatch[1]));
          height = Math.round(parseFloat(hMatch[1]));
        }
      }
      width = Math.max(400, Math.min(2400, width));
      height = Math.max(300, Math.min(2400, height));

      // Ensure explicit width/height
      svg = svg.replace(/<svg([^>]*)>/i, (m, attrs) => {
        const hasW = /\bwidth\b/i.test(attrs);
        const hasH = /\bheight\b/i.test(attrs);
        return `<svg${attrs}${hasW ? '' : ` width="${width}px"`}${hasH ? '' : ` height="${height}px"`}>`;
      });

      // Embed Font Awesome fonts as base64 so glyphs render correctly in PNG
      const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
      };

      const getDataUrl = async (path: string) => {
        const res = await fetch(path);
        if (!res.ok) throw new Error(`Failed to load font: ${path}`);
        const buf = await res.arrayBuffer();
        return `data:font/woff2;base64,${arrayBufferToBase64(buf)}`;
      };

      // Skip external fonts for PNG to avoid loading issues
      // Replace FontAwesome classes with simple fallback text
      svg = svg.replace(/class="fa[^"]*"/g, '');
      svg = svg.replace(/font-family="[^"]*Font Awesome[^"]*"/g, 'font-family="Arial, sans-serif"');
      
      const embeddedFontCss = `\n  <defs>\n    <style><![CDATA[\n      text, tspan { font-family: Arial, Helvetica, sans-serif; }\n    ]]></style>\n  </defs>\n`;

      if (svg.includes('<svg')) {
        svg = svg.replace(/<svg([^>]*)>/, (m) => `${m}\n${embeddedFontCss}`);
      }

      // Add white background
      svg = svg.replace(/<svg([^>]*)>/i, (m) => `${m}<rect width="100%" height="100%" fill="#ffffff"/>`);

      // Create canvas and draw SVG
      const scale = 2;
      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();

      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          try {
            ctx.setTransform(scale, 0, 0, scale, 0, 0);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            resolve();
          } catch (e) { reject(e as Error); }
        };
        img.onerror = () => reject(new Error('Failed to load SVG for rasterization'));
        img.src = url;
      });

      URL.revokeObjectURL(url);

      // Download as PNG
      canvas.toBlob((blobOut) => {
        if (!blobOut) {
          toast.error('Failed to create PNG blob');
          return;
        }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blobOut);
        a.download = `diagram-${index + 1}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(a.href), 100);
        toast.success('PNG downloaded with correct Font Awesome icons.');
      }, 'image/png');

    } catch (error) {
      console.error('PNG download failed:', error);
      toast.error(`PNG download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Project context helpers
  const getCurrentProjectId = () => {
    try { return sessionStorage.getItem('currentProjectId'); } catch { return null; }
  };

  const updateProjectWithVisuals = async (projectId: string, visuals: Array<{ svg: string; dataUrl?: string | null }>) => {
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('content_items')
        .select('data, version')
        .eq('id', projectId)
        .eq('user_id', user?.id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      if (!existing) throw new Error('Project not found');

      const existingData = (existing as any)?.data || {};
      const existingVisuals = Array.isArray(existingData.extractedVisuals) ? existingData.extractedVisuals : [];
      const nextVersionNumber = (existingVisuals.length || 0) + 1;

      const newVisualEntries = visuals.map(v => ({
        version: nextVersionNumber,
        svg: v.svg,
        dataUrl: v.dataUrl ?? null,
        created_at: new Date().toISOString()
      }));

      const mergedData = {
        ...existingData,
        extractedVisuals: [...existingVisuals, ...newVisualEntries],
      };

      const { error: updateError } = await supabase
        .from('content_items')
        .update({
          data: mergedData,
          version: ((existing as any)?.version ?? 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId)
        .eq('user_id', user?.id);
      if (updateError) throw updateError;

      toast.success(`Project updated with version ${nextVersionNumber} visuals!`);
      return { ok: true, version: nextVersionNumber } as const;
    } catch (error) {
      console.error('Error updating project with visuals:', error);
      return { ok: false } as const;
    }
  };

  const saveContent = async (content: string, type: 'text' | 'visual', originalPrompt: string) => {
    if (!user) return;

    try {
      // If part of an existing project, append visuals there instead of creating a new item
      if (type === 'visual') {
        const projectId = getCurrentProjectId();
        if (projectId) {
          const res = await updateProjectWithVisuals(projectId, [{ svg: content, dataUrl: null }]);
          if (res.ok) return;
        }
      }

      // Fallback: save as standalone content item
      const seriesId = crypto.randomUUID();
      const { error } = await supabase
        .from('content_items')
        .insert({
          user_id: user.id,
          kind: type === 'text' ? 'humanized' : 'visual',
          series_id: seriesId,
          version: 1,
          title: type === 'text' ? 'Humanized Text' : 'Visual Content',
          prompt: originalPrompt,
          data: type === 'text' ? { text: content } : { svg: content, dataUrl: null }
        });

      if (error) throw error;
      toast.success(`${type === 'text' ? 'Text' : 'Visual'} saved to My Content!`);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(`Failed to save ${type}`);
    }
  };

  const saveCompleteProject = async () => {
    if (!user) {
      toast.error("Please sign in to save content");
      return;
    }

    try {
      // Extract all SVGs from the humanized text
      const svgMatches = humanizedText.match(/<svg[\s\S]*?<\/svg>/g) || [];
      const extractedVisuals = svgMatches.map((svg, index) => ({
        id: `embedded_${index}`,
        svg: sanitizeSvg(svg),
        dataUrl: `data:image/svg+xml;utf8,${encodeURIComponent(sanitizeSvg(svg))}`
      }));

      const seriesId = crypto.randomUUID();
      const { data, error } = await supabase
        .from('content_items')
        .insert({
          user_id: user.id,
          kind: 'humanized',
          series_id: seriesId,
          title: 'Complete Project',
          prompt: originalText,
          version: 1,
          data: { 
            originalText: originalText,
            humanizedText: humanizedText,
            extractedVisuals: extractedVisuals,
            includeVisuals: includeVisuals
          }
        })
        .select('id')
        .single();

      if (error) throw error;
      try { sessionStorage.setItem('currentProjectId', data.id); } catch {}
      toast.success("Complete project saved to My Content!");
    } catch (error) {
      console.error('Error saving complete project:', error);
      toast.error('Failed to save complete project');
    }
  };

  const handleEdit = async (prompt: string, visualTypes?: string[], intensity?: string, options?: { mode: 'describe' | 'convert'; originalSvg?: string; direct?: boolean }) => {
    if (!user) {
      toast.error("Please sign in to edit content");
      return;
    }

    try {
      if (editType === 'visual' && options?.direct && options.originalSvg) {
        // Direct save from Visual Editor without calling AI
        const nextVersionNumber = internalVisualVersions.length;
        const editedSvg = options.originalSvg;
        const newVersion: VisualVersion = {
          id: `v${nextVersionNumber}-${Date.now()}`,
          svg: editedSvg,
          dataUrl: `data:image/svg+xml;utf8,${encodeURIComponent(editedSvg)}`,
          label: `v${nextVersionNumber}`,
          timestamp: Date.now()
        };
        
        // Add to versions and select it
        setInternalVisualVersions(prev => [...prev, newVersion]);
        setSelectedVersionIndex(internalVisualVersions.length);

        await saveContent(editedSvg, 'visual', prompt);
        onContentUpdate?.(editedSvg, 'visual');
      } else if (editType === 'visual' && currentVisual?.svg) {
        const result = await streamGenerateVisual(
          prompt,
          options?.mode === 'convert' ? (visualTypes?.[0] || 'auto') : 'auto',
          '4:3',
          { mode: options?.mode || 'describe', originalSvg: options?.originalSvg || currentVisual.svg, targetTypes: visualTypes }
        );
        if (result?.svg) {
          // Create new visual version
          const nextVersionNumber = internalVisualVersions.length;
          const newVersion: VisualVersion = {
            id: `v${nextVersionNumber}-${Date.now()}`,
            svg: result.svg,
            dataUrl: result.dataUrl || null,
            label: `v${nextVersionNumber}`,
            timestamp: Date.now()
          };
          
          // Add to versions and select it
          setInternalVisualVersions(prev => [...prev, newVersion]);
          setSelectedVersionIndex(internalVisualVersions.length);

          await saveContent(result.svg, 'visual', prompt);
          onContentUpdate?.(result.svg, 'visual');
        }
      } else if (editType === 'text') {
        const result = await streamHumanizeText(prompt, false, undefined, intensity);
        if (result) {
          await saveContent(result, 'text', prompt);
          onContentUpdate?.(result, 'text');
        }
      }
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error editing content:', error);
      toast.error('Failed to edit content');
    }
  };

  const openEditDialog = (type: 'text' | 'visual') => {
    setEditType(type);
    setIsEditDialogOpen(true);
  };

  const handleAdditionalVisualGenerate = async (prompt: string, visualType: string) => {
    if (!user) {
      toast.error("Please sign in to generate visuals");
      return;
    }

    try {
      const result = await streamGenerateVisual(prompt, visualType === 'auto' ? undefined : visualType);
      
      // If generation is successful, add to versions
      if (result?.svg) {
        const nextVersionNumber = internalVisualVersions.length;
        const newVersion: VisualVersion = {
          id: `v${nextVersionNumber}-${Date.now()}`,
          svg: result.svg,
          dataUrl: result.dataUrl || null,
          label: `v${nextVersionNumber}`,
          timestamp: Date.now()
        };
        
        setInternalVisualVersions(prev => [...prev, newVersion]);
        setSelectedVersionIndex(internalVisualVersions.length);
        
        await saveContent(result.svg, 'visual', prompt);
        onContentUpdate?.(result.svg, 'visual');
        
        setIsAdditionalVisualDialogOpen(false);
        toast.success("Additional visual generated successfully!");
      } else {
        throw new Error("No visual was generated");
      }
    } catch (error) {
      console.error('Error generating additional visual:', error);
      toast.error('Failed to generate additional visual');
    }
  };

  return (
    <div className={`space-y-6 animate-fade-in ${mobilePreview ? 'max-w-sm mx-auto' : ''}`}>
      {mobilePreview && (
        <div className="bg-muted/10 border border-border rounded-lg p-3 text-center">
          <p className="text-sm text-muted-foreground">📱 Mobile Preview Mode - Showing 360px width</p>
        </div>
      )}
      
      {/* Comparison Cards */}
      <div className={`grid ${mobilePreview ? 'grid-cols-1' : 'md:grid-cols-2'} gap-6`}>
        <Card className="bg-card border-border shadow-card">
          <CardHeader>
            <CardTitle className="text-lg text-muted-foreground">Original Text</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert prose-sm max-w-none">
              <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed text-sm">
                {originalText}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border shadow-card ring-2 ring-primary/20">
          <CardHeader>
            <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-2">
                <span className="text-lg text-primary">
                  {isStreaming ? "Streaming Text..." : isGeneratingVisuals ? "Generating Visuals..." : "Humanized Text"}
                </span>
                {(isStreaming || isGeneratingVisuals) && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
              <div className="flex gap-1 sm:gap-2 w-full sm:w-auto">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => copyToClipboard(humanizedText, 'humanized')}
                  className="flex-1 sm:flex-none justify-center"
                >
                  {copiedItems.has('humanized') ? (
                    <Check className="w-4 h-4 sm:mr-2" />
                  ) : (
                    <Copy className="w-4 h-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">Copy</span>
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={downloadAsFile}
                  className="flex-1 sm:flex-none justify-center"
                >
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Download</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog('text')}
                  className="flex-1 sm:flex-none justify-center"
                >
                  <Edit className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderContent(humanizedText)}
            
            {/* Feedback Buttons */}
            <div className="mt-4">
              <FeedbackButtons 
                contentItemId={getCurrentProjectId()} 
                onFeedbackSubmitted={() => {
                  // Optional: refresh or update UI after feedback
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Output Display */}
      <Card className="bg-card border-border shadow-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <CardTitle className="text-xl">Enhanced Output</CardTitle>
            <div className="flex gap-1 sm:gap-2 w-full sm:w-auto">
              <Button
                variant="secondary"
                onClick={() => copyToClipboard(humanizedText, 'full')}
                className="flex items-center gap-2 flex-1 sm:flex-none justify-center"
              >
                {copiedItems.has('full') ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Copy All</span>
              </Button>
              <Button
                variant="secondary"
                onClick={downloadAsFile}
                className="flex items-center gap-2 flex-1 sm:flex-none justify-center"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Download</span>
              </Button>
              {user && (
                <Button
                  variant="default"
                  onClick={saveCompleteProject}
                  className="flex items-center gap-2 flex-1 sm:flex-none justify-center"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Save Project</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="max-w-none">
          {renderContent(humanizedText)}
          
          {/* Feedback Buttons */}
          <div className="mt-6">
            <FeedbackButtons 
              contentItemId={getCurrentProjectId()} 
              onFeedbackSubmitted={() => {
                // Optional: refresh or update UI after feedback
              }}
            />
          </div>
          
          {/* Add Visual Button */}
          {includeVisuals && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setIsAdditionalVisualDialogOpen(true)}
                className="flex items-center gap-2"
                disabled={visualStreamingState.isGenerating}
              >
                {visualStreamingState.isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Generate Additional Visual
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <EditDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        type={editType}
        currentContent={editType === 'text' ? humanizedText : (currentVisual?.svg || '')}
        onEdit={handleEdit}
        isGenerating={editType === 'visual' ? visualStreamingState.isGenerating : textStreamingState.isStreaming}
        previewContent={editType === 'visual' ? visualStreamingState.svg : textStreamingState.content}
        title={`Edit ${editType === 'text' ? 'Text' : 'Visual'}`}
      />

      <AdditionalVisualDialog
        open={isAdditionalVisualDialogOpen}
        onOpenChange={setIsAdditionalVisualDialogOpen}
        onGenerate={handleAdditionalVisualGenerate}
        isGenerating={visualStreamingState.isGenerating}
        humanizedText={humanizedText}
      />
    </div>
  );
};